// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/setup/SetupCreateAdminPage.tsx
//
// Phase 1 Task 48 — Setup Wizard Step 1: create the founding superadmin.
//
// Per CONTEXT + API-CONTRACTS POST /api/v1/setup/init:
//   - email (must end with @printway.work — Zod refine)
//   - password (>= 12 chars)
//   - displayName (>= 1 char)
//   - acceptTerms (must be true — required by contract)
//
// Submits with `X-Setup-Token` header (read from sessionStorage; bootstrap
// done by SetupWizardLayout from the URL `?token=…` query param).
//
// On success → captures `totpEnrollmentToken` (and optional secret/URI if
// the BE response carries them — gracefully optional per task brief) into
// sessionStorage, then routes to `/setup/enable-2fa`.

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Input,
} from '@printway/ui';
import { resolveApiBaseUrl } from '@printway/shared/api';

import { useTranslation } from '../../i18n/useTranslation.js';
import { readSetupToken } from './SetupWizardLayout.js';

type TFunc = (key: string, vars?: Record<string, unknown>) => string;

// ---------------------------------------------------------------------------
// sessionStorage keys shared with downstream steps.
// ---------------------------------------------------------------------------

export const SETUP_ENROLL_TOKEN_KEY = 'setupTotpEnrollmentToken';
export const SETUP_TOTP_SECRET_KEY = 'setupTotpSecret';
export const SETUP_OTPAUTH_URI_KEY = 'setupOtpauthUri';
export const SETUP_USER_EMAIL_KEY = 'setupUserEmail';

// ---------------------------------------------------------------------------
// Zod schema — mirrors PostSetupInitRequest (API-CONTRACTS L86-91).
// ---------------------------------------------------------------------------

function buildCreateAdminSchema(t: TFunc) {
  return z
    .object({
      email: z
        .string()
        .min(1, t('setup.createAdmin.validation.emailRequired'))
        .email(t('setup.createAdmin.validation.emailInvalid'))
        .max(255)
        .refine((value) => value.toLowerCase().endsWith('@printway.work'), {
          message: t('setup.createAdmin.validation.emailDomain'),
        }),
      displayName: z
        .string()
        .min(1, t('setup.createAdmin.validation.nameRequired'))
        .max(120),
      password: z
        .string()
        .min(12, t('setup.createAdmin.validation.passwordMin'))
        .max(128, t('setup.createAdmin.validation.passwordMax')),
      confirmPassword: z.string(),
      acceptTerms: z.literal(true, {
        message: t('setup.createAdmin.validation.acceptTermsRequired'),
      } as never),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ['confirmPassword'],
      message: t('setup.createAdmin.validation.passwordMismatch'),
    });
}

type CreateAdminFormValues = z.infer<ReturnType<typeof buildCreateAdminSchema>>;

// ---------------------------------------------------------------------------
// API call — POST /api/v1/setup/init
// ---------------------------------------------------------------------------

interface SetupInitResponseData {
  userId: string;
  email: string;
  next: 'verify-2fa';
  totpEnrollmentToken: string;
  csrfToken: string;
  // Optional FE-helper fields (deviation note in SUMMARY): if BE emits the
  // raw secret + otpauth URI alongside the enrollment token, persist them
  // so step 2 can render the QR without an additional round-trip.
  totpSecret?: string;
  otpauthUri?: string;
}

interface ApiErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId?: string;
}

async function callSetupInit(
  payload: Omit<CreateAdminFormValues, 'confirmPassword'>,
  setupToken: string,
  t: (key: string, vars?: Record<string, unknown>) => string,
): Promise<SetupInitResponseData> {
  const baseUrl = resolveApiBaseUrl();
  const res = await fetch(`${baseUrl}/api/v1/setup/init`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Setup-Token': setupToken,
    },
    body: JSON.stringify(payload),
  });

  const json = (await res.json().catch(() => null)) as
    | { success: true; data: SetupInitResponseData }
    | ApiErrorEnvelope
    | null;

  if (!res.ok || !json || !('success' in json) || json.success !== true) {
    const err = json as ApiErrorEnvelope | null;
    const msg =
      err?.error?.message ?? t('setup.errors.httpFailed', { status: res.status });
    throw new Error(msg);
  }
  return json.data;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SetupCreateAdminPage(): JSX.Element {
  const { t } = useTranslation('ops');
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const createAdminSchema = useMemo(() => buildCreateAdminSchema(t), [t]);

  const form = useForm<CreateAdminFormValues>({
    resolver: zodResolver(createAdminSchema as never),
    defaultValues: {
      email: '',
      displayName: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false as unknown as true,
    },
    mode: 'onBlur',
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async (values: CreateAdminFormValues): Promise<void> => {
    setSubmitError(null);

    const setupToken = readSetupToken();
    if (!setupToken) {
      setSubmitError(t('setup.createAdmin.errors.missingToken'));
      return;
    }

    try {
      const { confirmPassword: _drop, ...payload } = values;
      void _drop;
      const data = await callSetupInit(payload, setupToken, t);

      // Persist hand-off context for step 2.
      try {
        window.sessionStorage.setItem(
          SETUP_ENROLL_TOKEN_KEY,
          data.totpEnrollmentToken,
        );
        window.sessionStorage.setItem(SETUP_USER_EMAIL_KEY, data.email);
        if (data.totpSecret) {
          window.sessionStorage.setItem(
            SETUP_TOTP_SECRET_KEY,
            data.totpSecret,
          );
        }
        if (data.otpauthUri) {
          window.sessionStorage.setItem(
            SETUP_OTPAUTH_URI_KEY,
            data.otpauthUri,
          );
        }
      } catch {
        // sessionStorage disabled — propagate via navigation state below.
      }

      navigate('/setup/enable-2fa', { replace: true });
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : t('setup.createAdmin.errors.createFailed'),
      );
    }
  };

  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold text-pw-text">
        {t('setup.createAdmin.heading')}
      </h2>
      <p className="mb-6 text-sm text-pw-text-muted">
        {t('setup.createAdmin.descriptionBefore')}{' '}
        <code>@printway.work</code>
        {t('setup.createAdmin.descriptionAfter')}
      </p>

      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField>
          <FormLabel htmlFor="setup-email">
            {t('setup.createAdmin.emailLabel')}
          </FormLabel>
          <FormControl>
            <Input
              id="setup-email"
              type="email"
              autoComplete="email"
              placeholder="founder@printway.work"
              aria-invalid={errors.email ? 'true' : 'false'}
              {...register('email')}
            />
          </FormControl>
          {errors.email && <FormMessage>{errors.email.message}</FormMessage>}
        </FormField>

        <FormField>
          <FormLabel htmlFor="setup-name">
            {t('setup.createAdmin.nameLabel')}
          </FormLabel>
          <FormControl>
            <Input
              id="setup-name"
              type="text"
              autoComplete="name"
              placeholder={t('setup.createAdmin.namePlaceholder')}
              aria-invalid={errors.displayName ? 'true' : 'false'}
              {...register('displayName')}
            />
          </FormControl>
          {errors.displayName && (
            <FormMessage>{errors.displayName.message}</FormMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="setup-password">
            {t('setup.createAdmin.passwordLabel')}
          </FormLabel>
          <FormControl>
            <Input
              id="setup-password"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.password ? 'true' : 'false'}
              {...register('password')}
            />
          </FormControl>
          {errors.password ? (
            <FormMessage>{errors.password.message}</FormMessage>
          ) : (
            <p className="mt-1 text-xs text-pw-text-muted">
              {t('setup.createAdmin.passwordHint')}
            </p>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="setup-password-confirm">
            {t('setup.createAdmin.confirmPasswordLabel')}
          </FormLabel>
          <FormControl>
            <Input
              id="setup-password-confirm"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.confirmPassword ? 'true' : 'false'}
              {...register('confirmPassword')}
            />
          </FormControl>
          {errors.confirmPassword && (
            <FormMessage>{errors.confirmPassword.message}</FormMessage>
          )}
        </FormField>

        <FormField>
          <label className="flex items-start gap-2 text-sm text-pw-text">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-pw-border text-pw-primary focus:ring-pw-primary/40"
              {...register('acceptTerms')}
            />
            <span>{t('setup.createAdmin.acceptTerms')}</span>
          </label>
          {errors.acceptTerms && (
            <FormMessage>{errors.acceptTerms.message}</FormMessage>
          )}
        </FormField>

        {submitError && (
          <div
            role="alert"
            className="rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300"
          >
            {submitError}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? t('setup.createAdmin.submitting')
              : t('setup.createAdmin.submit')}
          </Button>
        </div>
      </Form>
    </div>
  );
}
